// Client for the OAuth Authorization Server's interaction endpoints
// (apps/api/src/oauth/interactions.ts). These need BOTH the session bearer
// token (who is consenting) and oidc-provider's interaction cookie (which
// request is being answered) — hence `credentials: 'include'`. In dev the
// Vite server proxies /oauth so this stays same-origin.

import { API_BASE_URL } from './config';

export interface InteractionDetails {
  prompt: 'login' | 'consent' | string;
  clientId: string;
  clientName: string | null;
  /** false = the client registered itself; nobody vouched for its name. */
  verified: boolean;
  redirectUri: string | null;
  scopes: string[];
  /** Account the interaction is bound to (after the login step). */
  accountId: string | null;
}

const call = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('sss.accessToken');
  const res = await fetch(`${API_BASE_URL}/oauth/interaction/${path}`, {
    ...init,
    credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error_description?: string };
    throw new Error(body.message || body.error_description || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
};

export const getInteraction = (uid: string) => call<InteractionDetails>(`${encodeURIComponent(uid)}/details`);

const finish = (uid: string, step: 'login' | 'confirm' | 'deny') =>
  call<{ redirectTo: string }>(`${encodeURIComponent(uid)}/${step}`, { method: 'POST', body: '{}' });

export const bindInteraction = (uid: string) => finish(uid, 'login');
export const approveInteraction = (uid: string) => finish(uid, 'confirm');
export const denyInteraction = (uid: string) => finish(uid, 'deny');

/** sessionStorage flag: "the user chose another account for interaction <uid>" — rebind after re-login. */
export const SWITCH_ACCOUNT_KEY = 'sss.oauth.switchAccount';
